import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { SCHEDULE, VENUES } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

function MapArt() {
  return (
    <svg viewBox="0 0 800 500" className="h-full w-full" aria-hidden="true">
      <rect x="16" y="16" width="768" height="468" rx="24" fill="none" stroke="rgba(212,175,55,0.25)" strokeWidth="1.5" />
      <ellipse cx="210" cy="140" rx="190" ry="105" fill="#1B4D3E" opacity="0.18" />
      <ellipse cx="620" cy="380" rx="210" ry="120" fill="#1B4D3E" opacity="0.14" />
      <ellipse cx="120" cy="420" rx="130" ry="80" fill="#1B4D3E" opacity="0.12" />
      <rect x="340" y="180" width="190" height="120" rx="10" fill="#D4AF37" opacity="0.10" stroke="#D4AF37" strokeOpacity="0.4" />
      <path d="M80 265 C 220 245, 300 305, 400 295 S 650 325, 762 255" stroke="rgba(253,251,247,0.15)" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M405 70 C 395 160, 415 225, 402 300 S 380 415, 425 470" stroke="rgba(253,251,247,0.12)" strokeWidth="6" fill="none" strokeLinecap="round" />
      {[[90, 90], [150, 60], [280, 110], [680, 90], [730, 150], [60, 250], [740, 320], [180, 460], [560, 130], [640, 230]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="5" fill="#1B4D3E" opacity="0.55" />
      ))}
      <text x="400" y="480" textAnchor="middle" fill="#D4AF37" opacity="0.55" fontSize="18" letterSpacing="8" fontFamily="Cinzel, serif">CHANDBAGH</text>
      <text x="758" y="52" textAnchor="middle" fill="#D4AF37" fontSize="20" fontFamily="Cinzel, serif">N</text>
      <path d="M758 60 l-6 18 h12 z" fill="#D4AF37" opacity="0.8" />
    </svg>
  );
}

export default function CampusMap({ activeVenue, onExplore }) {
  return (
    <section id="map" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          eyebrow="Venue Explorer"
          title="Tour the Chandbagh Estate"
          sub="Tap a pin on the map — or pick a venue below — and the schedule filters to that corner of campus."
        />
        <div className="grid min-w-0 grid-cols-1 items-start gap-10 lg:grid-cols-[1.15fr_.85fr]">
          <Reveal className="relative aspect-[16/10] overflow-hidden rounded-3xl border border-line/20 bg-panel/40" y={36}>
            <MapArt />
            {VENUES.map((v) => {
              const active = activeVenue === v.name;
              return (
                <button
                  key={v.slug}
                  style={{ left: `${v.x}%`, top: `${v.y}%` }}
                  onClick={() => onExplore(active ? null : v.name)}
                  data-testid={`map-pin-${v.slug}`}
                  aria-label={v.name}
                  className="group absolute -translate-x-1/2 -translate-y-1/2"
                >
                  <span className={`relative flex h-4 w-4 items-center justify-center ${active ? "scale-125" : ""} transition-transform duration-300`}>
                    <span className="pulse-ring absolute inset-0 rounded-full" style={{ background: active ? "#D4AF37" : "#1B4D3E" }} />
                    <span className={`relative h-3 w-3 rounded-full ${active ? "bg-gold" : "bg-forest ring-2 ring-ivory/60"}`} />
                  </span>
                  <span className={`glass absolute left-1/2 top-5 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${active ? "text-gold" : "text-fg group-hover:text-gold"}`}>
                    {v.name}
                  </span>
                </button>
              );
            })}
          </Reveal>

          <div className="min-w-0 space-y-4 overflow-hidden" data-testid="map-venue-list">
            {VENUES.map((v, i) => {
              const active = activeVenue === v.name;
              const count = SCHEDULE.filter((e) => e.venue === v.name).length;
              return (
                <Reveal key={v.slug} delay={i * 0.05} y={20}>
                  <button
                    onClick={() => onExplore(active ? null : v.name)}
                    data-testid={`map-venue-${v.slug}`}
                    className={`glass flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-all duration-300 hover:-translate-y-0.5 ${active ? "border-gold/60 shadow-[0_0_30px_rgba(212,175,55,0.15)]" : "hover:border-gold/30"}`}
                  >
                    <img src={v.img} alt={v.name} loading="lazy" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                    <span className="min-w-0 flex-1">
                      <span className={`block font-display text-base font-bold ${active ? "text-gold" : "text-fg"}`}>{v.name}</span>
                      <span className="mt-0.5 block truncate text-xs text-soft">{v.blurb}</span>
                      <span className="mt-1 block font-mono text-[10px] tracking-[0.2em] text-soft/70">{count} SCHEDULED EVENT{count === 1 ? "" : "S"}</span>
                    </span>
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${active ? "bg-gold text-navy" : "text-gold"}`}>
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </button>
                </Reveal>
              );
            })}
            <Reveal delay={0.3} y={20}>
              <button
                onClick={() => { onExplore(null); scrollToId("#schedule"); }}
                data-testid="map-view-all-events"
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gold/40 py-3.5 text-xs font-bold tracking-[0.25em] text-gold transition-all duration-300 hover:bg-gold/10"
              >
                <MapPin className="h-4 w-4" /> VIEW FULL SCHEDULE
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
